import React, { useEffect, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import { useSetRecoilState } from 'recoil';

import { ConsentParams } from '~/shared/types';
import { consentSelector } from '~/widget/state/widgetState';
import normalizeConsentParams from '~/shared/utils/normalizeConsentParams';
import Icon from '../Icon';
import CookieSettingsModal from './components/CookieSettingsModal';
import { pluginConfig } from './config';
import createConsentHandlers from './utils/createConsentHandlers';
import consentParamsToCategories from './utils/consentParamsToCategories';
import useLocale from '~/widget/hooks/useLocale';
import safeLocalStorage from '~/widget/utils/safeLocalStorage';
import syncConsentCategoriesToBanner from './utils/syncConsentCategoriesToBanner';
import { BNDL_COOKIE_CONSENT_STORAGE_KEY } from '../WithConfig';
import Button from '../Button';

const text = {
  updateCookieSettings: {
    nl: 'Cookie-instellingen',
    en: 'Cookie preferences',
  },
};

// This key is used by library itself and is being stored in the cookies
export const BNDL_STANDALONE_COOKIE_CONSENT_STORAGE_KEY = '_bndl_standalone_cc';

const CookieBanner: React.FC = () => {
  const locale = useLocale();
  const theme = useTheme();

  const setConsentState = useSetRecoilState(consentSelector);
  const [hasStoredConsent, setHasStoredConsent] = useState<boolean>(
    () => safeLocalStorage.getItem(BNDL_COOKIE_CONSENT_STORAGE_KEY) != null,
  );

  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    let initialConsent: ConsentParams | null = null;
    try {
      const existing = safeLocalStorage.getItem(
        BNDL_COOKIE_CONSENT_STORAGE_KEY,
      );

      if (existing) {
        // Keep Recoil in sync on initial load when banner is not shown
        const parsed = JSON.parse(existing) as ConsentParams;
        // Normalize/guard consent: ensure predictable shape and allowed values ('granted' | 'denied').
        // Handles partial/legacy payloads and coerces invalid/missing keys to 'denied'. If already valid, it's a no-op.
        const normalized = normalizeConsentParams(parsed);
        initialConsent = normalized;
        setConsentState(normalized);
        setHasStoredConsent(true);
      }
    } catch {}

    const handlers = createConsentHandlers({
      storageKey: BNDL_COOKIE_CONSENT_STORAGE_KEY,
      setConsentState,
      setHasStoredConsent,
    });

    CookieConsent.run(
      pluginConfig({
        COOKIE_CONSENT_STORAGE_KEY: BNDL_STANDALONE_COOKIE_CONSENT_STORAGE_KEY,
        language: locale,
        ...handlers,
      }),
    );

    // If consent exists, sync it into CookieConsent library to prevent any auto-open or mismatches
    // Defer hide() call to ensure CookieConsent.run() has finished initializing
    if (initialConsent) {
      const categories = consentParamsToCategories(initialConsent);
      syncConsentCategoriesToBanner(categories);
      // Use requestAnimationFrame to ensure library is fully initialized before hiding
      requestAnimationFrame(() => {
        CookieConsent.hide();
      });
    }
  }, [locale]);

  // Auto-open the consent banner if no consent stored yet
  useEffect(() => {
    if (!hasStoredConsent) CookieConsent.show();
  }, [hasStoredConsent]);

  return (
    <>
      <RoundCookieButton
        borderRadius="full"
        onClick={() => setShowSettings(true)}
        aria-label={text.updateCookieSettings[locale]}
      >
        <Icon
          name="cookies-locked"
          strokeWidth={2}
          color={theme.color('primary', 'text')}
        />
      </RoundCookieButton>

      {showSettings && (
        <CookieSettingsModal
          locale={locale}
          cookiePreferences={CookieConsent.getUserPreferences()}
          timestamp={CookieConsent.getCookie()?.lastConsentTimestamp ?? ''}
          consentId={CookieConsent.getCookie()?.consentId ?? ''}
          onClose={() => setShowSettings(false)}
          onShowPreferences={() => CookieConsent.showPreferences()}
        />
      )}
    </>
  );
};

const RoundCookieButton = styled(Button)(
  ({ theme }) => css`
    position: fixed;
    margin: 0 auto;
    width: 100%;
    height: 100%;
    max-height: 50px;
    border-radius: ${theme.getSystem().border.radius.full};
    max-width: 50px;
    bottom: 30px;
    left: 25px;
    box-shadow: ${theme.boxShadow('card')};

    svg {
      margin-top: 5px;
      width: 1.5em;
    }
  `,
);

export default CookieBanner;
